import { invoke } from '@tauri-apps/api/core';
import * as audioDB from '../utils/audioDB.js';
import * as fontDB from '../utils/fontDB.js';

/**
 * Persistence of the Designer's resource libraries (datasets, audio, fonts,
 * synths).
 *
 * In the desktop app (Tauri) they live in the app's SQLite store
 * (haspdesktop: resource_libraries, resource_files). In the browser they stay
 * in localStorage, with audio and font bytes in IndexedDB
 * (src/utils/audioDB.js, src/utils/fontDB.js).
 *
 * The first desktop start after this change copies the browser-stored
 * library into SQLite once (see migrateOnce); the browser copy is left
 * untouched as a backup.
 */
export const isDesktop = () => typeof window !== 'undefined' && '__TAURI__' in window;

const migrationFlag = library => `haspdesigner_sqlite_migrated_${library}`;

function readLocal(storageKey) {
    try {
        const raw = localStorage.getItem(storageKey);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * Copy the browser-stored library into an empty SQLite library, once.
 * `migrateEntries(entries)` may rewrite entries on the way (e.g. move audio
 * bytes and remap their storageId).
 */
async function migrateOnce(library, storageKey, migrateEntries) {
    if (localStorage.getItem(migrationFlag(library))) return;
    const stored = await invoke('get_resource_library_command', { library });
    if (stored.length === 0) {
        let entries = readLocal(storageKey);
        if (entries.length > 0) {
            if (migrateEntries) entries = await migrateEntries(entries);
            await invoke('save_resource_library_command', { library, entries });
        }
    }
    localStorage.setItem(migrationFlag(library), new Date().toISOString());
}

/** All entries of a library, in saved order. */
export async function loadResourceLibrary(library, storageKey, { migrateEntries } = {}) {
    if (!isDesktop()) return readLocal(storageKey);
    await migrateOnce(library, storageKey, migrateEntries);
    return invoke('get_resource_library_command', { library });
}

// Desktop writes are queued so a later save can never land before an earlier one.
let writeQueue = Promise.resolve();

/** Save a library as a whole. */
export function saveResourceLibrary(library, storageKey, entries) {
    const snapshot = JSON.parse(JSON.stringify(entries));
    if (!isDesktop()) {
        localStorage.setItem(storageKey, JSON.stringify(snapshot));
        return Promise.resolve();
    }
    writeQueue = writeQueue
        .then(() => invoke('save_resource_library_command', { library, entries: snapshot }))
        .catch(error => console.warn(`Failed to save the ${library} library:`, error));
    return writeQueue;
}

// --- Resource file bytes (the `storageId` of an audio or font resource) ---

// Browser storage per kind of file-backed resource.
const BROWSER_FILES = {
    audio: { add: audioDB.addAudioFile, get: audioDB.getAudioFile, remove: audioDB.deleteAudioFile },
    fonts: { add: fontDB.addFontFile, get: fontDB.getFontFile, remove: fontDB.deleteFontFile },
};

/** Store a file's bytes (base64 data URL). Returns its storage id. */
export function addResourceFile(kind, data, originalFilename, type) {
    if (!isDesktop()) return BROWSER_FILES[kind].add(data, originalFilename, type);
    return invoke('add_resource_file_command', { kind, file: { data, originalFilename, type: type ?? null } });
}

/** `{ data, originalFilename, type }`, or null/undefined when absent. */
export function getResourceFile(kind, id) {
    if (!isDesktop()) return BROWSER_FILES[kind].get(id);
    return invoke('get_resource_file_command', { kind, id: Number(id) });
}

export function deleteResourceFile(kind, id) {
    if (!isDesktop()) return BROWSER_FILES[kind].remove(id);
    return invoke('delete_resource_file_command', { kind, id: Number(id) });
}

export const addAudioFile = (data, originalFilename, type) => addResourceFile('audio', data, originalFilename, type);
export const getAudioFile = id => getResourceFile('audio', id);
export const deleteAudioFile = id => deleteResourceFile('audio', id);
export const addFontFile = (data, originalFilename, type) => addResourceFile('fonts', data, originalFilename, type);
export const getFontFile = id => getResourceFile('fonts', id);
export const deleteFontFile = id => deleteResourceFile('fonts', id);

/**
 * Migration step for a file-backed library: move each entry's bytes from
 * IndexedDB into SQLite and point its storageId at the new row. Entries that
 * share bytes (duplicates) keep sharing them.
 */
export function migrateFilesToDesktop(kind) {
    return async entries => {
        const moved = new Map();
        const result = [];
        for (const entry of entries) {
            if (entry.storageId === undefined || entry.storageId === null) {
                result.push(entry);
                continue;
            }
            if (!moved.has(entry.storageId)) {
                const file = await BROWSER_FILES[kind].get(entry.storageId);
                // A missing file becomes null: the old IndexedDB id could name
                // a different SQLite row.
                moved.set(entry.storageId, file
                    ? await invoke('add_resource_file_command', {
                        kind,
                        file: { data: file.data, originalFilename: file.originalFilename ?? entry.filename ?? '', type: file.type ?? null },
                    })
                    : null);
            }
            result.push({ ...entry, storageId: moved.get(entry.storageId) });
        }
        return result;
    };
}

export const migrateAudioFilesToDesktop = migrateFilesToDesktop('audio');
